import {Component, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import {Subscription} from 'rxjs';
import {Broadcast} from 'src/app/models/Broadcast';
import {BroadcastStatusTone, PENDING, broadcastStatusTone} from 'src/app/models/BroadcastStatus';
import {User} from 'src/app/models/User';
import {ToastService} from 'src/app/shared/components/toast/toast.service';
import {AuthService} from 'src/app/shared/services/auth.service';
import {BroadcastsService, ValidationQueueResult} from 'src/app/shared/services/broadcasts.service';
import {ADMINISTRATOR} from 'src/app/shared/services/user.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';

export type ValidationTab = 'pending' | 'approved' | 'rejected';

export const VALIDATION_TABS: {key: ValidationTab; label: string}[] = [
  {key: 'pending', label: 'Pendente análise'},
  {key: 'approved', label: 'Aprovados'},
  {key: 'rejected', label: 'Reprovados'},
];

/**
 * Validação de SMS Massivo, layout v2 (CU-17tm466nzee, 14/10/2026).
 *
 * Regras que não mudam (só o layout mudou — ver `sms-validation.component.html`):
 * - `GET /broadcasts/list/validate` via `BroadcastsService.getAllBroadcastsToValidate`, com
 *   os mesmos parâmetros de busca (`start_date`/`end_date`/`author`/`name`/`order_by`/
 *   `inverted_order`).
 * - Aprovar/Cancelar só habilitados quando `broadcast_status_id === PENDING` ("Pendente
 *   Análise") — e só aparecem na aba "Pendente análise".
 * - Modal de cancelamento: Justificativa obrigatória, senão nada chama a API — o texto é
 *   "Justificativa é obrigatória!".
 * - `getMessage`/`getDate` idênticos ao componente antigo (o segundo, de propósito, sem hora
 *   no `date` pipe — evita o bug de fuso já conhecido).
 * - Refresh automático a cada 10s; `loading` (esqueleto) só na primeira carga e na troca de aba.
 *
 * Review do MR !558 (ação 9): UMA busca por vez. Cada `listPaginated()` cancela a anterior
 * (`listRequest`) — sem isso, trocar de aba rápido (ou o refresh de 10s cair no meio) deixava
 * duas buscas no ar e a que respondesse por último (às vezes a da aba ANTIGA) sobrescrevia o
 * resultado. O refresh automático não cancela uma busca em andamento (só pula a vez), senão um
 * backend lento nunca terminaria nenhuma. Falha (500, status 0/rede) vira estado de erro
 * visível (`loadError`) com "Tentar de novo", em vez de esqueleto eterno ou lista velha calada.
 *
 * Ajuste do Bruno em HML (14/10/2026): o switch "Somente pendentes de análise" virou 3 abas
 * (`VALIDATION_TABS`) — `pending_only` saiu do filtro, entrou `tab` (`pending`/`approved`/
 * `rejected`), com contador por aba vindo no `counts` da resposta (`ValidationQueueResult`).
 * Aprovados mostram quem aprovou e quando (`broadcast.reviewer`/`reviewed_at`); Reprovados
 * mostram quem reprovou, quando e o motivo (`reason`, mesma justificativa do cancelar).
 * Broadcasts de antes desta mudança não têm essas colunas preenchidas — o template mostra
 * "—", nunca inventa dado.
 *
 * O que mudou nesta reescrita (fora o HTML/SCSS):
 * - Período (`startDate`/`endDate`) agora é string `yyyy-MM-dd` nativa do `<input type="date">`
 *   direto no form, sem o datepicker do Material e sem conversão: já é o formato que a API
 *   espera, então sumiu o `formatDate()`/os campos soltos `startDate`/`endDate` da classe que
 *   só existiam para essa conversão.
 * - `filter.orderDirection = filter.invertedOrder` deixou de estar dentro de um `if`: com
 *   `invertedOrder = false` o código antigo nunca escrevia `orderDirection`, e o
 *   `BroadcastsService` mandava `inverted_order=undefined` pro backend.
 * - As duas assinaturas de `valueChanges` (`startDate`/`endDate` → `validatePeriod()`) saíram
 *   de dentro de `listPaginated()` (chamada a cada refresh de 10s) para o `ngOnInit`: do jeito
 *   antigo, cada chamada criava uma assinatura nova nunca cancelada.
 * - `pageChanged(page)` agora realmente atualiza `this.page` antes de recarregar — no
 *   componente antigo a paginação montava uma `queryParams` que não era usada em lugar
 *   nenhum e `this.page` nunca mudava.
 * - Usuário (nome/e-mail) e a justificativa de cancelamento são bindings normais do Angular
 *   (nenhum `[innerHTML]`).
 */
@Component({
  selector: 'app-sms-validation',
  templateUrl: './sms-validation.component.html',
  styleUrls: ['./sms-validation.component.scss']
})
export class SmsValidationComponent implements OnInit, OnDestroy {
  @ViewChild('modalValidation')
  modalValidation: TemplateRef<any>;
  modalValidationRef: any;

  reason = '';
  reasonError = false;
  broadcast: Broadcast;

  invalidTime = false;
  autoRefreshController: any = null;

  readonly tabs = VALIDATION_TABS;
  activeTab: ValidationTab = 'pending';
  counts: {pending: number; approved: number; rejected: number} = {pending: 0, approved: 0, rejected: 0};

  page = 1;
  pageSize = 10;
  originalUser: User;
  user: User;
  loading: boolean;
  result: ValidationQueueResult;
  /** Mensagem do estado de erro da lista (`null` = sem erro). */
  loadError: string | null = null;
  subscriptions: Subscription[] = [];
  /** A busca da lista em andamento — uma por vez (ver comentário da classe). */
  private listRequest: Subscription | null = null;

  broadcastForm: UntypedFormGroup;

  get administrator(): boolean {
    return this.originalUser.profile_id === ADMINISTRATOR;
  }

  constructor(
    private broadcastsService: BroadcastsService,
    private route: ActivatedRoute,
    private toastService: ToastService,
    private authService: AuthService,
    private modalService: NgbModal,
    private formBuilder: UntypedFormBuilder,
  ) { }

  ngOnInit(): void {
    this.broadcastForm = this.formBuilder.group({
      startDate: [null],
      endDate: [null],
      text: [''],
      author: [''],
      orderBy: ['created_at'],
      invertedOrder: [true]
    });

    this.user = this.authService.getUser();

    this.subscriptions.push(
      this.route.queryParams.subscribe(params => {
        this.page = params.page || this.page;
        this.pageSize = params.per_page || this.pageSize;

        this.broadcastForm.patchValue({
          startDate: params.startDate || null,
          endDate: params.endDate || null,
          text: params.text || '',
          author: params.author || '',
          orderBy: params.orderBy || 'created_at',
          invertedOrder: params.invertedOrder !== undefined ? params.invertedOrder === 'true' : true,
        });
        this.validatePeriod();

        this.loading = true;
        this.listPaginated();
      }),
      this.authService.userChanged.subscribe(() => this.listPaginated()),
      this.broadcastsService.importFinished.subscribe(() => this.listPaginated()),
      this.broadcastForm.get('startDate').valueChanges.subscribe(() => this.validatePeriod()),
      this.broadcastForm.get('endDate').valueChanges.subscribe(() => this.validatePeriod()),
    );

    this.autoRefreshController = setInterval(() => this.autoRefresh(), 10 * 1000);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.listRequest?.unsubscribe();

    if (this.autoRefreshController) {
      clearInterval(this.autoRefreshController);
      this.autoRefreshController = null;
    }
  }

  listPaginated(): void {
    const filter: any = {...this.broadcastForm.value};

    this.user = this.authService.getUser();
    if (this.user && !this.user.validated && !this.administrator) {
      return;
    }

    this.originalUser = this.authService.getUser(true);

    filter.orderDirection = filter.invertedOrder;
    filter.tab = this.activeTab;

    // Uma busca por vez: a anterior (outra aba/filtro/página, ou um refresh) é cancelada e a
    // resposta dela nunca chega a sobrescrever a desta.
    this.listRequest?.unsubscribe();
    // `loading` não é desligado num `finalize`: cancelar a busca anterior dispararia o
    // `finalize` dela e apagaria o esqueleto com a busca nova ainda no ar.
    this.listRequest = this.broadcastsService
      .getAllBroadcastsToValidate(this.page, this.pageSize, filter)
      .subscribe({
        next: result => {
          this.loading = false;
          this.loadError = null;
          this.result = result;
          this.counts = result.counts ?? this.counts;
        },
        error: (err: HttpErrorResponse) => {
          this.loading = false;
          this.loadError = err?.status === 0
            ? 'Sem resposta do servidor. Confira sua conexão e tente de novo.'
            : 'Não foi possível carregar os envios para validação.';
        },
      });
  }

  /** "Tentar de novo" do estado de erro: volta o esqueleto e refaz a mesma busca. */
  retry(): void {
    this.loading = true;
    this.listPaginated();
  }

  /**
   * Refresh de 10s: pula a vez se a busca anterior ainda não respondeu — cancelá-la (como faz
   * uma busca pedida pelo usuário) deixaria um backend mais lento que 10s sem resposta nunca.
   */
  autoRefresh(): void {
    if (this.listRequest && !this.listRequest.closed) {
      return;
    }
    this.listPaginated();
  }

  validatePeriod(): void {
    const startDate = this.broadcastForm.get('startDate').value;
    const endDate = this.broadcastForm.get('endDate').value;

    this.invalidTime = !!(startDate && endDate && startDate > endDate);
  }

  invertOrder(): void {
    const newValue = !this.broadcastForm.get('invertedOrder').value;
    this.broadcastForm.patchValue({invertedOrder: newValue});
    this.listPaginated();
  }

  search(): void {
    this.page = 1;
    this.listPaginated();
  }

  clearFilters(): void {
    this.broadcastForm.reset({
      startDate: null,
      endDate: null,
      text: '',
      author: '',
      orderBy: 'created_at',
      invertedOrder: true,
    });
    this.page = 1;
    this.listPaginated();
  }

  pageChanged(page: number): void {
    if (page) {
      this.page = page;
    }
    this.listPaginated();
  }

  isPendingReview(broadcast: Broadcast): boolean {
    return broadcast.broadcast_status_id === PENDING;
  }

  statusTone(broadcast: Broadcast): BroadcastStatusTone {
    return broadcastStatusTone(broadcast.broadcast_status_id);
  }

  approve(broadcast: Broadcast): void {
    this.broadcastsService.approveBroadcast(broadcast.id).subscribe({
      next: () => {
        this.toastService.success('Broadcast aprovado com sucesso!');
        this.listPaginated();
      },
      error: (err: HttpErrorResponse) => this.handleReviewActionError(err, 'aprovar'),
    });
  }

  setTab(tab: ValidationTab): void {
    if (this.activeTab === tab) {
      return;
    }
    this.activeTab = tab;
    this.page = 1;
    // Esqueleto na troca de aba: o que está na tela é da aba anterior (e o template já mostra
    // os campos da aba nova — "Aprovado por", "Motivo"…), não pode ficar à mostra até responder.
    this.loading = true;
    this.listPaginated();
  }

  getMessage(broadcast: Broadcast): string {
    let message = broadcast.message;
    if (message.includes('{meu_link}')) {
      const myLink = broadcast.short_url || broadcast.url;
      message = message.replace('{meu_link}', myLink);
    }

    return message;
  }

  cancel(broadcast: Broadcast): void {
    this.reason = '';
    this.reasonError = false;
    this.broadcast = broadcast;
    this.modalValidationRef = this.modalService.open(this.modalValidation, { size: 'lg' });
  }

  disapprove(): void {
    this.reasonError = !this.reason || this.reason.trim().length === 0;
    if (this.reasonError) {
      return;
    }

    this.broadcastsService.disapproveBroadcast(this.broadcast.id, this.reason).subscribe({
      next: () => {
        this.toastService.success('Broadcast cancelado com sucesso!');
        this.listPaginated();
      },
      error: (err: HttpErrorResponse) => this.handleReviewActionError(err, 'cancelar'),
      complete: () => this.modalValidationRef.dismiss()
    });
  }

  getDate(broadcast: Broadcast): Date | string {
    return broadcast.scheduled_date || broadcast.created_at;
  }

  /**
   * 409 do back (CU-17tm466nzee, revisão do MR !1734 — compare-and-set em approve/cancel):
   * outro admin já decidiu este broadcast entre a tela carregar e o clique. A lista recarrega
   * para sair do estado obsoleto; qualquer outro erro só avisa, sem recarregar.
   */
  private handleReviewActionError(err: HttpErrorResponse, action: 'aprovar' | 'cancelar'): void {
    const fallback = action === 'aprovar' ? 'Erro ao aprovar o Broadcast!' : 'Erro ao cancelar o Broadcast!';
    this.toastService.error(err?.status === 409 ? (err.error?.message || fallback) : fallback);
    if (err?.status === 409) {
      this.listPaginated();
    }
  }
}
